import { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  MapPin,
  HelpCircle,
  Compass,
  ArrowRight,
  Send,
  Phone,
} from "lucide-react";
import PackageQuiz from "@/components/landing/PackageQuiz";
import PricingFAQModal from "@/components/landing/PricingFAQModal";
import ContactFormModal from "@/components/landing/ContactFormModal";
import {
  FloatingOrbs,
  LightLeak,
  CornerMic,
  AmbientDust,
  ScrubLine,
  MiniEqualizer,
  CircularText,
  FrequencyTicker,
} from "@/components/landing/AmbientMotion";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Contact = () => {
  const [quizOpen, setQuizOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-28"
    >
      <FloatingOrbs colors={["#00f0ff", "#ff0055"]} />
      <AmbientDust count={24} color="#00f0ff" />
      <FrequencyTicker
        items={["LAKELAND FL", "ZIP 33801", "EASTERN TIME", "SB-STUDIO", "24/7 DM"]}
        corner="tr"
        color="#00f0ff"
        duration={40}
      />
      <div className="absolute -bottom-8 right-8 pointer-events-none">
        <CircularText text="LET'S MAKE SOMETHING LOUD · " size={180} color="#ff0055" duration={35} />
      </div>
      <div className="absolute inset-0 -z-10">
        <div className="absolute bottom-[0%] left-[20%] w-[700px] h-[700px] rounded-full bg-[#00f0ff]/10 blur-[160px]" />
        <div className="absolute bottom-[-20%] right-[10%] w-[600px] h-[600px] rounded-full bg-[#ff0055]/15 blur-[160px]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-12 gap-6 md:gap-10 items-start"
        >
          {/* Header column */}
          <motion.div
            variants={reveal}
            custom={0}
            className="col-span-12 lg:col-span-5"
          >
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#00f0ff] mb-5 inline-flex items-center gap-3">
              [ 05 · Contact ]
              <MiniEqualizer color="#00f0ff" />
            </div>
            <h2
              data-testid="contact-heading"
              className="font-display font-black tracking-tighter text-[44px] md:text-[64px] lg:text-[72px] leading-[0.95]"
            >
              Let&apos;s make{" "}
              <span className="italic font-light text-[#a395c3]">
                something
              </span>{" "}
              <span className="text-[#00f0ff] text-glow-cyan">loud.</span>
            </h2>
            <p className="text-[#a395c3] text-base md:text-lg mt-7 max-w-md leading-relaxed">
              Book a free 30-min discovery call, or pick a side door — the
              quiz maps your lane, the FAQ answers what everyone asks, or
              drop us a line and we&apos;ll take it from there.
            </p>
            <div className="mt-6 max-w-[240px]">
              <ScrubLine color="#00f0ff" duration={3.6} />
            </div>
          </motion.div>

          {/* Right column — Book-a-call hero pill + secondary pills */}
          <motion.div
            variants={reveal}
            custom={1}
            className="col-span-12 lg:col-span-7 space-y-6"
          >
            {/* HERO CTA: Book a discovery call — opens the modal */}
            <button
              type="button"
              id="discovery"
              onClick={() =>
                window.dispatchEvent(new CustomEvent("sb:open-discovery"))
              }
              data-testid="contact-cta-discovery"
              className="w-full max-w-full group relative rounded-3xl p-5 md:p-8 text-left overflow-hidden glow-border bg-[#11091a]/70 backdrop-blur-md hover:-translate-y-0.5 transition-transform"
            >
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#00f0ff]/25 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full bg-[#ff0055]/15 blur-3xl pointer-events-none" />
              <LightLeak color="#ff8a1e" corner="tr" size={240} />
              <CornerMic color="#00f0ff" corner="br" size={30} />

              <div className="relative flex items-center justify-between gap-3 md:gap-6">
                <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-[#00f0ff]/15 border border-[#00f0ff]/30 text-[#00f0ff] flex items-center justify-center flex-shrink-0">
                    <Phone size={18} />
                  </div>
                  <div className="min-w-0 max-w-full">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#00f0ff] mb-2 whitespace-nowrap">
                      <span className="relative flex w-1.5 h-1.5">
                        <span className="absolute inline-flex w-full h-full rounded-full bg-[#22c55e] opacity-70 animate-ping" />
                        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                      </span>
                      Free · 30 min
                    </div>
                    <div className="font-display font-black text-xl md:text-3xl tracking-tight leading-tight break-words">
                      Book a discovery call.{" "}
                      <span className="italic font-light text-[#a395c3]">
                        On us.
                      </span>
                    </div>
                    <div className="text-[#a395c3] text-xs md:text-sm mt-2 max-w-md">
                      Pick a slot in our calendar — we&apos;ll follow up by
                      phone or Google Meet.
                    </div>
                  </div>
                </div>
                <span className="hidden sm:flex w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#00f0ff] text-[#05050a] items-center justify-center flex-shrink-0 group-hover:rotate-[-45deg] transition-transform">
                  <ArrowRight size={20} />
                </span>
              </div>
            </button>

            {/* Studio session / Spotlight guest secondary row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("sb:open-booking"))
                }
                data-testid="contact-cta-book-session"
                className="group inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-white text-[#05050a] font-semibold text-[13px] hover:bg-[#00f0ff] transition-colors"
              >
                Book a studio session
                <ArrowRight
                  size={14}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </button>
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("sb:open-guest"))
                }
                data-testid="contact-cta-guest"
                className="group inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-transparent border border-white/20 text-white font-semibold text-[13px] hover:border-[#ff0055] hover:text-[#ff0055] transition-colors"
              >
                Request Spotlight guest spot
                <ArrowRight
                  size={14}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </button>
            </div>
          </motion.div>

          {/* Full-width bottom row — 5 equal-size explore pills */}
          <motion.div
            variants={reveal}
            custom={2}
            className="col-span-12 pt-2"
          >
            <div className="flex items-center gap-4 mb-5" aria-hidden="true">
              <span className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]/70">
                Or explore
              </span>
              <span className="flex-1 h-px bg-white/10" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {/* Studio location — opens Maps modal */}
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("sb:open-location"))
                }
                data-testid="contact-cta-location"
                className="group relative aspect-square text-left rounded-2xl p-5 bg-white/[0.03] border border-white/10 hover:border-[#ff0055]/50 hover:bg-white/[0.06] transition-all overflow-hidden flex flex-col justify-between"
              >
                <div className="w-11 h-11 rounded-xl bg-[#ff0055]/15 border border-[#ff0055]/30 text-[#ff0055] flex items-center justify-center group-hover:scale-105 transition-transform">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="font-display font-bold text-[15px] md:text-base tracking-tight">
                    Studio location
                  </div>
                  <div className="text-[#a395c3] text-xs mt-1 leading-relaxed">
                    Lakeland, FL
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] text-[#ff5577]">
                    View map
                    <ArrowRight
                      size={11}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </div>
                </div>
              </button>

              {/* Email */}
              <a
                href="mailto:info@sbmediastudio.com"
                data-testid="contact-cta-email"
                className="group relative aspect-square text-left rounded-2xl p-5 bg-white/[0.03] border border-white/10 hover:border-white/40 hover:bg-white/[0.06] transition-all overflow-hidden flex flex-col justify-between"
              >
                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 text-white flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="font-display font-bold text-[15px] md:text-base tracking-tight">
                    Email us
                  </div>
                  <div className="text-[#a395c3] text-xs mt-1 leading-relaxed break-all">
                    info@sbmediastudio.com
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] text-white/80">
                    Compose
                    <ArrowRight
                      size={11}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </div>
                </div>
              </a>

              {/* Take the quiz */}
              <button
                type="button"
                onClick={() => setQuizOpen(true)}
                data-testid="contact-cta-quiz"
                className="group relative aspect-square text-left rounded-2xl p-5 bg-white/[0.03] border border-white/10 hover:border-[#00f0ff]/50 hover:bg-white/[0.06] transition-all overflow-hidden flex flex-col justify-between"
              >
                <div className="w-11 h-11 rounded-xl bg-[#00f0ff]/15 border border-[#00f0ff]/30 text-[#00f0ff] flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Compass size={18} />
                </div>
                <div>
                  <div className="font-display font-bold text-[15px] md:text-base tracking-tight">
                    Take the quiz
                  </div>
                  <div className="text-[#a395c3] text-xs mt-1 leading-relaxed">
                    5 questions — find your fit.
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] text-[#00f0ff]">
                    Start
                    <ArrowRight
                      size={11}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </div>
                </div>
              </button>

              {/* Pricing FAQ */}
              <button
                type="button"
                onClick={() => setFaqOpen(true)}
                data-testid="contact-cta-faq"
                className="group relative aspect-square text-left rounded-2xl p-5 bg-white/[0.03] border border-white/10 hover:border-[#a78bfa]/50 hover:bg-white/[0.06] transition-all overflow-hidden flex flex-col justify-between"
              >
                <div className="w-11 h-11 rounded-xl bg-[#a78bfa]/15 border border-[#a78bfa]/30 text-[#c4b5fd] flex items-center justify-center group-hover:scale-105 transition-transform">
                  <HelpCircle size={18} />
                </div>
                <div>
                  <div className="font-display font-bold text-[15px] md:text-base tracking-tight">
                    Pricing FAQ
                  </div>
                  <div className="text-[#a395c3] text-xs mt-1 leading-relaxed">
                    10 answers to what everyone asks.
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] text-[#c4b5fd]">
                    Read
                    <ArrowRight
                      size={11}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </div>
                </div>
              </button>

              {/* Contact us */}
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                data-testid="contact-cta-us"
                className="group relative aspect-square text-left rounded-2xl p-5 bg-white/[0.03] border border-white/10 hover:border-[#ff0055]/50 hover:bg-white/[0.06] transition-all overflow-hidden flex flex-col justify-between"
              >
                <div className="w-11 h-11 rounded-xl bg-[#ff0055]/15 border border-[#ff0055]/30 text-[#ff0055] flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Send size={18} />
                </div>
                <div>
                  <div className="font-display font-bold text-[15px] md:text-base tracking-tight">
                    Contact us
                  </div>
                  <div className="text-[#a395c3] text-xs mt-1 leading-relaxed">
                    Send a note — reply within 24 hrs.
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] text-[#ff5577]">
                    Open form
                    <ArrowRight
                      size={11}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </div>
                </div>
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <PackageQuiz open={quizOpen} onClose={() => setQuizOpen(false)} />
      <PricingFAQModal open={faqOpen} onClose={() => setFaqOpen(false)} />
      <ContactFormModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </section>
  );
};

export default Contact;
